/**
 * The one file in the SPA that is allowed to know what the product is called.
 *
 * HANDOFF §1.1. Its counterpart is `backend/config/brand.php`; between them they
 * are the entire surface a rebrand touches, plus the four files in
 * `public/brand/`.
 *
 * The literals below are fallbacks for a build with no environment - `npm run
 * dev` outside Compose, a CI type-check. They are not the source of truth, and
 * a hit for "Vibed" anywhere else under `src/` is a bug rather than a style
 * preference.
 */

export const brand = {
  /**
   * The name in a sentence: "Join Vibed", "Reset your Vibed password".
   *
   * Distinct from the wordmark below, which is the mark rather than the name.
   * Prose that shouts the mark reads like a billboard in the middle of a
   * paragraph, and a mark written in sentence case is not the mark.
   */
  name: import.meta.env.VITE_BRAND_NAME ?? 'Vibed',

  /**
   * The mark, set as text.
   *
   * For the few places the identity is presented rather than mentioned - the
   * badge on an embedded player, the provider an oEmbed consumer credits, the
   * splash the desktop app shows while it checks for an update. Uppercase, and
   * the dot is never dropped.
   */
  wordmark: import.meta.env.VITE_BRAND_WORDMARK ?? 'VIBED.FM',

  domain: import.meta.env.VITE_BRAND_DOMAIN ?? 'vibed.fm',
  url: import.meta.env.VITE_APP_URL ?? 'https://vibed.fm',
  support: import.meta.env.VITE_SUPPORT_EMAIL ?? 'support@getvibed.live',

  /**
   * Fixed generic paths. A rebrand replaces the files; nothing in code ever
   * references `getvibed-logo.svg`, so nothing has to be found and updated.
   */
  logo: '/brand/logo.svg',
  logoMark: '/brand/mark.svg',
  favicon: '/brand/favicon.svg',
  ogImage: '/brand/og.png',

  /**
   * Where media comes from.
   *
   * Two hosts because they have different rules: the public bucket is pure edge
   * cache with no gate on it, and audio sits behind a Worker that checks a token
   * before it will hand over a segment (HANDOFF §4.4). Reading both from here
   * means the media host is never hardcoded into a component - which matters
   * because a rebrand moves them, being subdomains of the brand domain.
   */
  cdnUrl: import.meta.env.VITE_CDN_PUBLIC_URL ?? 'https://cdn.vibed.fm',
} as const;

/** Absolute URL for an object in the public assets bucket. */
export function cdn(key: string): string {
  return `${brand.cdnUrl}/${key.replace(/^\/+/, '')}`;
}
